import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../api/config';
import type { CustomerUser, Customer, AuthResponse } from '../types';

interface AuthContextType {
  user: CustomerUser | null;
  customer: Customer | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mustChangePassword: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setPassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<CustomerUser | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const navigate = useNavigate();

  const refreshUser = useCallback(async () => {
    try {
      const response = await apiClient.get<{ user: CustomerUser; customer: Customer }>(
        API_ENDPOINTS.me
      );
      setUser(response.user);
      setCustomer(response.customer);
      setMustChangePassword(response.user.mustChangePassword);
    } catch {
      setUser(null);
      setCustomer(null);
      apiClient.clearTokens();
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('portal_access_token');
    if (token) {
      refreshUser().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [refreshUser]);

  const login = async (email: string, password: string) => {
    const response = await apiClient.post<AuthResponse>(API_ENDPOINTS.login, {
      email,
      password,
    });

    localStorage.setItem('portal_access_token', response.accessToken);
    localStorage.setItem('portal_refresh_token', response.refreshToken);

    setUser(response.user);
    setCustomer(response.customer);
    setMustChangePassword(response.mustChangePassword);

    if (response.mustChangePassword) {
      navigate('/set-password');
    } else {
      navigate('/');
    }
  };

  const logout = async () => {
    try {
      await apiClient.post(API_ENDPOINTS.logout, {});
    } catch {
      // Ignore logout errors
    } finally {
      apiClient.clearTokens();
      setUser(null);
      setCustomer(null);
      setMustChangePassword(false);
      navigate('/login');
    }
  };

  const setPassword = async (currentPassword: string, newPassword: string) => {
    await apiClient.post(API_ENDPOINTS.setPassword, {
      currentPassword,
      newPassword,
    });

    setMustChangePassword(false);
    await refreshUser();
    navigate('/');
  };

  const value = {
    user,
    customer,
    isAuthenticated: !!user,
    isLoading,
    mustChangePassword,
    login,
    logout,
    setPassword,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
